import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import { GolfScoringProvider } from "./GolfScoringContext";
import GolfScoringButton from "./GolfScoringButton";
import {
  ArrowLeft,
  Globe,
  Lock,
  MapPin,
  Calendar,
  Clock,
  Users,
  Share2,
  MoreVertical,
  Pencil,
  Trash2,
  X,
  Image as ImageIcon,
  Navigation2,
  Tag
} from "lucide-react";

const ActivityInfo = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Hardcoded coordinates (you might want to get these from the activity object)
  const coordinates = {
    lat: 29.106360194695505,
    lng: -110.93477210180153
  };

  const openGoogleMaps = () => {
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const docRef = doc(db, "activities", uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setActivity({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError("Activity not found");
        }
      } catch (err) {
        console.error("Error fetching activity:", err);
        setError("Error loading activity");
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [uid]);

  const handleDeleteActivity = async () => {
    try {
      setDeletingActivity(true);

      // Primero eliminar la imagen de Storage si existe
      if (activity.coverImage) {
        const imageRef = ref(storage, `activities/${uid}/cover`);
        try {
          await deleteObject(imageRef);
        } catch (error) {
          console.error("Error deleting image:", error);
        }
      }

      // Luego eliminar el documento
      const activityRef = doc(db, "activities", uid);
      await deleteDoc(activityRef);
      navigate("/myactivities", { replace: true });
    } catch (err) {
      console.error("Error deleting activity:", err);
      setError("Failed to delete activity. Please try again.");
      setDeletingActivity(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.menu-container')) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const OptionsMenu = () => (
    <div className="absolute top-12 right-3 bg-white rounded-xl shadow-lg py-2 min-w-[160px] z-50 menu-container">
      <button
        onClick={() => {
          setShowMenu(false);
          navigate(`/edit/${activity.id}`);
        }}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
      >
        <Pencil className="w-4 h-4" />
        Edit Activity
      </button>
      <button
        onClick={() => {
          setShowMenu(false);
          setShowDeleteConfirm(true);
        }}
        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
      >
        <Trash2 className="w-4 h-4" />
        Delete Activity
      </button>
    </div>
  );

  const DeleteConfirmation = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium">Delete Activity</h3>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this activity? This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="flex-1 py-2 px-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            disabled={deletingActivity}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteActivity}
            disabled={deletingActivity}
            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {deletingActivity ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-orange-500 hover:text-orange-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!activity) return null;

  const eventDate = new Date(activity.eventDateTime);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const formattedTime = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con imagen */}
      <div className="relative mb-8">
        {/* Imagen de portada */}
        <div className="relative w-full h-56">
          {activity.coverImage && !imageError ? (
            <img
              src={activity.coverImage}
              alt={activity.title}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
          )}
          {/* Overlay gradiente */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent">
            <div className="flex items-center justify-between py-2 px-3">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                <button 
                  className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white"
                  onClick={() => alert('Share functionality coming soon!')}
                >
                  <Share2 className="w-5 h-5" />
                </button>
                {activity.creatorUID === user?.uid && (
                  <div className="relative menu-container">
                    <button
                      onClick={() => setShowMenu(!showMenu)}
                      className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    {showMenu && <OptionsMenu />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 -mt-10">
        {/* Title Section */}
        <div className="bg-white p-4 rounded-xl shadow-sm space-y-2">
          <h1 className="text-xl font-semibold">{activity.title}</h1>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {activity.isPublic ? (
                <>
                  <Globe className="w-4 h-4 text-green-500" />
                  <span>Public Activity</span>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 text-blue-500" />
                  <span>Private Activity</span>
                </>
              )}
            </div>
            {activity.category && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
                <Tag className="w-4 h-4 text-gray-500" />
                <span>{activity.category}</span>
              </div>
            )}
          </div>
        </div>

        {/* Location and Time */}
        <div className="bg-white p-4 rounded-xl shadow-sm space-y-4">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-grow">
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-medium">{activity.location}</p>
            </div>
            <button 
              onClick={openGoogleMaps}
              className="bg-orange-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
            >
              <Navigation2 className="w-4 h-4" />
              Navigate
            </button>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-600">Date</p>
              <p className="font-medium">{formattedDate}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-600">Time</p>
              <p className="font-medium">{formattedTime}</p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <p className="whitespace-pre-wrap">{activity.description}</p>
        </div>

        {/* Golf Scoring Section */}
        {activity.category === 'Golf' && (
  <div className="bg-white p-4 rounded-xl shadow-sm">
    <GolfScoringProvider activityId={uid}>
      <div className="flex justify-center">
        <GolfScoringButton activityId={uid} />
      </div>
    </GolfScoringProvider>
  </div>
)}

        {/* Attendees */}
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-400" />
              <span className="font-medium">Attendees</span>
            </div>
            <span className="text-sm text-gray-500">
              {activity.attendees.length} / {activity.maxAttendees}
            </span>
          </div>

          {/* Creator info */}
          <div className="flex items-center gap-3">
            <img
              src={activity.creatorPhoto || "https://via.placeholder.com/40"}
              alt={activity.creatorName}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <p className="font-medium">{activity.creatorName}</p>
              <p className="text-sm text-gray-500">Organizer</p>
            </div>
          </div>
        </div>
      </div>

      {/* Join Button (if not creator) */}
      {activity.creatorUID !== user?.uid && (
        <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-gray-100">
          <button className="w-full bg-orange-500 text-white py-3 rounded-xl font-medium hover:bg-orange-600 transition-colors">
            Join Activity
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && <DeleteConfirmation />}
    </div>
  );
};

export default ActivityInfo;